/**
 * @module builder
 */
import type { OptionsDocument, PresentationIndustry, PresentationOptionV2, PresentationPageV2 } from 'paintscout'
import { uuid } from '../../../util'
import { PRESENTATION_V2_SCHEMA_VERSION } from '../../../quote/presentation/is-presentation-next'
import { createPresentationNextPage } from '../presentation-next-tree'

/**
 * Creates a new, empty v2 ("PresentationNext") presentation.
 *
 * The v1 counterpart (createPresentationOption) derives from getDefaultPresentation and produces a
 * two-level document, so v2 needs its own: it must carry `schemaVersion` — the discriminator every
 * v2 doc is required to have (see isPresentationV2Doc) — and a `pages → sections → blocks` tree.
 *
 * Starts as a **single page**, which is what makes a fresh presentation render as one continuous
 * scroll with the nav built from section titles. Additional pages are an explicit user action.
 */
export function createPresentationNextOption(args?: {
  options?: OptionsDocument
  label?: string
  /** Prefab layout pages to start from; defaults to one empty page. */
  pages?: PresentationPageV2[]
}): PresentationOptionV2 {
  const { options, label = 'New Presentation', pages } = args ?? {}

  return {
    key: uuid(),
    label,
    schemaVersion: PRESENTATION_V2_SCHEMA_VERSION,
    active: true,
    // organically-created v2 docs carry no meaningful quoteTypes, but updatePresentationOption
    // dereferences the field unconditionally
    quoteTypes: ['all'],
    pages: pages?.length ? pages : [createPresentationNextPage({ label })],
    advanced: {
      industry: options?.options?.companyIndustry?.value as PresentationIndustry
    }
  }
}
